import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef, Input, ChangeDetectorRef } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { ViewState } from '../../../state/view.state';
import { ViewUIStatus } from '../../../models/view-ui-status.enum';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ViewService } from '../../../view.service';
import { ViewToolsMode } from '../../../models/view-tools-mode.enum';
import { trigger, transition, style, animate } from '@angular/animations';
import { ViewActions } from '../../../state/view.actions';
import { ViewUI } from '../../../models/view-ui.enum';
import { ViewUIService } from '../../../view-ui.service';
import { MediaEnum } from 'src/app/story/models/media.enum';
import { Breakpoint } from 'src/app/breakpoint/models/breakpoint.enum';
import { BreakpointState } from 'src/app/breakpoint/state/breakpoint.state';
import { ToolEnum } from 'src/app/engine/models/tool.enum';

@Component({
  selector: 'app-view-toolbar',
  templateUrl: './view-toolbar.component.html',
  styleUrls: ['./view-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(200px)'
        }),
        animate('300ms ease-in-out', style({
          opacity: 1,
          transform: '*'
        }))
      ]),
      transition(':leave', [
        animate('300ms ease-in-out', style({
          transform: 'translateY(200px)',
          opacity: 0
        }))
      ])
    ])
  ]
})
export class ViewToolbarComponent implements OnInit {
  viewUI = ViewUI;

  // ---------- Child Elements ----------
  @ViewChild('viewToolbar', {static: false }) viewToolbar: ElementRef;



  // ---------- Input / Output ----------
  @Input()
  canExit = true;

  @Input()
  mode = ViewToolsMode.Main;

  @Input()
  lockMode = false;

  @Input()
  position: 'center' | 'right' = 'center';


  // ---------- Class Variables ----------
  ngUnsubscribe = new Subject();
  breakpoint = Breakpoint;
  viewToolsMode = ViewToolsMode;
  viewLayout = ViewUI;
  mediaTemplate: MediaEnum;

  collapsePagination = false;
  collapseScrubber = false;



  // ---------- Lifecycle hooks ----------
  constructor(
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private viewService: ViewService,
    private viewUIService: ViewUIService
  ) { }

  ngOnInit() {
    this.initLayoutListener();
    this.initBreakpointListener();
  }

  initLayoutListener() {
    this.store.select(ViewState.getUI)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((layout) => {
        if (this.lockMode) {
          return;
        }

        this.mode = ViewToolsMode.Main;

        if (this.viewUIService.isUIVisible(ViewUI.Media)) {
          this.mediaTemplate = layout.media.template;
          this.mode = ViewToolsMode.Media;
        }

        if (this.viewUIService.isToolsVisible()) {
          this.mode = ViewToolsMode.Tools;
        }

        if (
          this.viewUIService.isVisibilityLayersVisible() && 
          this.viewUIService.isUIVisible(ViewUI.VisibilityLayers)
        ) {
          this.mode = ViewToolsMode.VisibilityLayers;
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  initBreakpointListener() {
    this.store.select(BreakpointState.getBreakpoint)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(breakpoint => {
        const outOfSpace = breakpoint <= Breakpoint.Small;
        this.collapseScrubber = outOfSpace;
        this.collapsePagination = outOfSpace;
        
        this.changeDetectorRef.markForCheck();
      });
  }

  isVisible(layout: ViewUI) {
    return this.viewUIService.isVisible(layout);
  }

  isEnabled(layout: ViewUI) {
    return this.viewUIService.isUIEnabled(layout);
  }


  // ---------- Interactions ----------
  handleExitClick() {
    const layout = this.store.selectSnapshot(ViewState.getUI);
    layout.media.status = ViewUIStatus.Hide;
    layout.tools.status = ViewUIStatus.Hide;
    layout.visibilityLayers.status = ViewUIStatus.Hide;
      // Revert tool selection when tools isn't visible
    this.viewService.selectTool(ToolEnum.Select);
    this.store.dispatch(new ViewActions.SetUI(layout));
  }

  handleExploreToolsClick() {
    this.viewService.toggleToolbar('tools');
  }
}
